import { useRef, useState } from "react";
import { motion, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import useMotionPreference from "@/hooks/useMotionPreference";
import useScrollStage from "@/hooks/useScrollStage";
import { MagneticLink } from "@/lib/motion";
import { ArrowRight, Eye } from "lucide-react";
import { trackResumeDownload } from "@/lib/analytics";
import { MaskLine } from "./HeroCodeSnippet";
import HeroCodeWindow from "./HeroCodeWindow";
import HeroAboutScene from "./HeroAboutScene";
import SceneNextLink from "./SceneNextLink";

export default function Hero() {
  const ref = useRef(null);
  const reduceMotion = useMotionPreference();
  const cinematic = useScrollStage();
  const { scrollYProgress } = useScroll({ target: ref, offset: cinematic ? ["start start", "end end"] : ["start start", "end start"] });
  // The opening scroll segment hands the code window over to the About story.
  const handoffProgress = useTransform(scrollYProgress, [0, 1 / 3.5], [0, 1]);
  const heroProgress = cinematic ? handoffProgress : scrollYProgress;
  const yText = useTransform(heroProgress, [0, 1], [0, -60]);
  const opacity = useTransform(heroProgress, cinematic ? [0, 0.24, 0.48] : [0, 0.8], cinematic ? [1, 1, 0] : [1, 0]);
  const introGlowOpacity = useTransform(scrollYProgress, [0.18, 0.3], [1, 0]);
  const [aboutPhase, setAboutPhase] = useState(false);
  useMotionValueEvent(scrollYProgress, "change", value => setAboutPhase(value >= 0.29));

  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const tickingRef = useRef(false);
  const onMouse = (e) => {
    // Disable on touch devices (mobile)
    if (reduceMotion || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

    if (!tickingRef.current) {
      const clientX = e.clientX;
      const clientY = e.clientY;
      tickingRef.current = true;
      requestAnimationFrame(() => {
        const { innerWidth, innerHeight } = window;
        if (innerWidth && innerHeight) {
          setTilt({
            x: (clientX / innerWidth - 0.5) * 20,
            y: (clientY / innerHeight - 0.5) * 20,
          });
        }
        tickingRef.current = false;
      });
    }
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMouse}
      data-testid="hero"
      className={cinematic ? "relative h-[350svh]" : "relative flex min-h-screen flex-col justify-center overflow-hidden pb-20 pt-24 lg:py-32"}
    >
      {cinematic && <div id="about" aria-hidden="true" className="absolute left-0 top-[105svh] h-px w-px" />}
      <div className={cinematic ? "sticky top-0 flex h-svh flex-col justify-center overflow-hidden pb-20 pt-24 lg:py-32" : "contents"}>
      {cinematic && <HeroAboutScene progress={scrollYProgress} stageRef={ref} />}
      {cinematic && !aboutPhase && <SceneNextLink href="#about" label="About" className="z-30" />}
      {/* Animated gradient blobs - Reduced blur on mobile */}
      <motion.div
        style={{ x: reduceMotion ? 0 : tilt.x, y: reduceMotion ? 0 : tilt.y, opacity: cinematic ? introGlowOpacity : 1 }}
        className="pointer-events-none absolute -left-40 top-20 h-[500px] w-[500px] rounded-full bg-brand-600/20 blur-[60px] md:blur-[130px] animate-floaty"
      />
      <motion.div
        style={{ x: reduceMotion ? 0 : -tilt.x, y: reduceMotion ? 0 : -tilt.y, opacity: cinematic ? introGlowOpacity : 1 }}
        className="pointer-events-none absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-brand-500/10 blur-[50px] md:blur-[120px] animate-floaty2"
      />

      <div inert={cinematic && aboutPhase} aria-hidden={cinematic && aboutPhase} style={{ pointerEvents: cinematic && aboutPhase ? "none" : "auto" }} className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-12 items-center gap-y-8 px-6 md:px-10 lg:gap-8">
        {/* Left */}
        <motion.div style={{ y: reduceMotion ? 0 : yText, opacity: reduceMotion ? 1 : opacity }} className="col-span-12 lg:col-span-7">

          <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tighter text-white sm:text-5xl md:text-6xl lg:text-7xl">
            <MaskLine i={0}>
              <span className="text-white">Tanishq Saini</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={reduceMotion ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reduceMotion ? 0 : 0.7, duration: reduceMotion ? 0 : 0.8 }}
            className="mt-6 max-w-xl text-lg font-medium text-white md:text-xl"
          >
            Software Developer building{" "}
            <span className="text-brand-400">full-stack applications</span>{" "}
            and{" "}
            <span className="text-brand-400">backend systems</span>
          </motion.p>

          <motion.p
            initial={reduceMotion ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reduceMotion ? 0 : 0.85, duration: reduceMotion ? 0 : 0.8 }}
            className="mt-5 max-w-xl text-base leading-relaxed text-white/50"
          >
            I build web applications with React, Next.js, TypeScript, Node.js, and Spring Boot,
            from subscription services and authenticated APIs to offline-first experiences and
            real-time workflows. My internships at The Lev Labs and Eklavya gave me hands-on
            production delivery experience.
          </motion.p>

          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reduceMotion ? 0 : 1, duration: reduceMotion ? 0 : 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <MagneticLink
              href="#projects"
              data-testid="hero-view-projects"
              className="group inline-flex items-center gap-2 rounded-full bg-brand-solid px-7 py-3.5 text-base font-medium text-brand-contrast transition-all duration-200 hover:bg-brand-solid-hover hover:shadow-[0_0_30px_rgb(var(--brand-500)/0.4)]"
            >
              View Projects
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </MagneticLink>
            <MagneticLink
              href="/TanishqSainiResume.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-download-resume"
              onClick={() => trackResumeDownload("hero")}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 text-base font-medium text-white transition-all duration-200 hover:border-white/30 hover:bg-white/10"
            >
              <Eye className="h-4 w-4" />
              View Resume
            </MagneticLink>
          </motion.div>
        </motion.div>

        <HeroCodeWindow cinematic={cinematic} handoffProgress={handoffProgress} />
      </div>

      <motion.div
        style={{ opacity: reduceMotion ? 1 : opacity }}
        className="hidden lg:block absolute bottom-8 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/30"
      >
        Scroll to explore
      </motion.div>
      </div>
    </section>
  );
}
